import React from 'react';
import { SparkleIcon } from '@phosphor-icons/react';

interface SkeletonBlockProps {
    x: number;
    y: number;
    size: number;
    isGenerating: boolean;
}

export const SkeletonBlock: React.FC<SkeletonBlockProps> = ({
    x,
    y,
    size,
    isGenerating
}) => {
    return (
        <div
            className="absolute z-20 pointer-events-none"
            style={{
                left: x,
                top: y,
                width: size,
                height: size
            }}
        >
            {/* Skeleton Frame */}
            <div className="w-full h-full rounded-lg border-2 border-dashed border-indigo-500/50 bg-indigo-500/5">
                {/* Animated Background Pattern */}
                <div
                    className="absolute inset-0 rounded-lg overflow-hidden"
                    style={{
                        background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(99, 102, 241, 0.05) 10px, rgba(99, 102, 241, 0.05) 20px)'
                    }}
                />

                {/* Center Content */}
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                    {isGenerating ? (
                        <>
                            {/* Spinning Loader */}
                            <div className="relative w-10 h-10">
                                <div className="absolute inset-0 border-2 border-indigo-500/30 rounded-full" />
                                <div className="absolute inset-0 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                                <SparkleIcon className="absolute inset-2 w-6 h-6 text-indigo-400" />
                            </div>
                            <span className="text-xs text-indigo-400 font-medium">Generating...</span>
                        </>
                    ) : (
                        <>
                            <div className="w-8 h-8 border-2 border-dashed border-indigo-500/50 rounded-lg flex items-center justify-center">
                                <SparkleIcon className="w-4 h-4 text-indigo-400/50" />
                            </div>
                            <span className="text-xs text-indigo-400/50">New Block</span>
                        </>
                    )}
                </div>

                {/* Corner Markers */}
                <div className="absolute top-1 left-1 w-3 h-3 border-l-2 border-t-2 border-indigo-500/50 rounded-tl" />
                <div className="absolute top-1 right-1 w-3 h-3 border-r-2 border-t-2 border-indigo-500/50 rounded-tr" />
                <div className="absolute bottom-1 left-1 w-3 h-3 border-l-2 border-b-2 border-indigo-500/50 rounded-bl" />
                <div className="absolute bottom-1 right-1 w-3 h-3 border-r-2 border-b-2 border-indigo-500/50 rounded-br" />
            </div>

            {/* Pulse Animation (when generating) */}
            {isGenerating && (
                <div className="absolute inset-0 rounded-lg border-2 border-indigo-500/30 animate-ping" />
            )}
        </div>
    );
};
